import { useContext, useEffect, useRef } from 'react';
import {
  SupportContext,
  type SupportContextValue,
} from '../contexts/supportContextValue';

/**
 * Read the current support context. Used by the SupportModal.
 */
export function useSupportContext(): SupportContextValue {
  const state = useContext(SupportContext);
  if (!state) {
    return {};
  }
  return state.context;
}

/**
 * Set support context for the current page. Automatically clears on unmount.
 * Call this near the top of page components to provide screen-specific context.
 */
export function useSetSupportContext(ctx: SupportContextValue): void {
  const state = useContext(SupportContext);
  // Serialize only JSON-safe fields for change detection (label may be ReactNode)
  const serialized = JSON.stringify({
    subject: ctx.subject,
    hiddenContext: ctx.hiddenContext,
    recipient: ctx.recipient,
  });

  // Keep stable refs so the effect only depends on serialized content, not
  // the context state object (which changes on every setContext call).
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    stateRef.current?.setContext(ctxRef.current);
    return () => {
      stateRef.current?.clearContext();
    };
  }, [serialized]);
}
